import { Component, OnInit, computed } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ConfirmationDialog } from '../../../shared/components/confirmation-dialog/confirmation-dialog.component';
import { LogFileViewerComponent } from '../../../shared/components/log-file-viewer/log-file-viewer.component';
import { InterfaceImport } from '../../../shared/models/common.models';
import { AuthorisationService } from '../../../shared/services/authorisation.service';
import { CommonService } from '../../../shared/services/common.service';
import { ConfigurationService } from '../../../shared/services/configuration.service';
import { ControlPanelService } from './control-panel.service';


@Component({
  templateUrl: 'control-panel.component.html',
  styleUrls: ['./control-panel.component.scss'],
  standalone: true,
  imports: [FormsModule, ReactiveFormsModule, MatButtonModule, MatFormFieldModule, MatInputModule, MatDatepickerModule]
})
export class ControlPanelComponent implements OnInit
{
  public form: UntypedFormGroup;

  private dialogIsOpen = false;

  // Expose the settings signal for the template
  public readonly settings = this.configurationService.cbSettings;

  public readonly showInterfacing = computed(() =>
  {
    const s = this.settings();
    return !!(s.isInterfacingClientCategories
      || s.isInterfacingRates
      || s.isInterfacingUsers
      || s.isInterfacingActivities
      || s.isInterfacingClients);
  });

  constructor(public fb: UntypedFormBuilder, private controlPanelService: ControlPanelService,
    private commonService: CommonService,
    public configurationService: ConfigurationService,
    private confirmDialog: MatDialog,
    public authorisationService: AuthorisationService,
    private dialog: MatDialog
  )
  {
    this.form = this.fb.group({
      ClientsStartDate: [new Date()],
      ActivityStartDate: [new Date()]
    });
  }

  ngOnInit()
  {

  }

  ResetActivitiesCache()
  {
    this.controlPanelService.resetActivitiesCache().then(result =>
    {
      let msg = "Unable to reset activities cache.";

      if (result == true)
      {
        msg = "Activities cache was successfully reset.";
      }

      this.commonService.notify("Reset Cache", msg, result);
    });
  }

  ResetPropertiesCache()
  {
    this.controlPanelService.resetPropertiesCache().then(result =>
    {
      let msg = "Unable to reset properties cache.";

      if (result == true)
      {
        msg = "Properties cache was successfully reset.";
      }

      this.commonService.notify("Reset Cache", msg, result);
    });
  }

  ResetCategoriesCache()
  {
    this.controlPanelService.resetCategoriesCache().then(result =>
    {
      let msg = "Unable to reset categories cache.";

      if (result == true)
      {
        msg = "Categories cache was successfully reset.";
      }

      this.commonService.notify("Reset Cache", msg, result);
    });
  }

  ResetClientsCache()
  {
    this.controlPanelService.resetClientsCache().then(result =>
    {
      let msg = "Unable to reset clients cache.";

      if (result == true)
      {
        msg = "Clients cache was successfully reset.";
      }

      this.commonService.notify("Reset Cache", msg, result);
    });
  }

  ResetProductCategoriesCache()
  {
    this.controlPanelService.resetProductCategoriesCache().then(result =>
    {
      let msg = "Unable to reset client categories cache.";

      if (result == true)
      {
        msg = "Client Categories cache was successfully reset.";
      }

      this.commonService.notify("Reset Cache", msg, result);
    });
  }

  PurgeAuditTrail()
  {
    this.controlPanelService.purgeAuditTrail().then(result =>
    {
      let msg = "Unable to purge audit trail.";

      if (result == true)
      {
        msg = "Audit trail was successfully purged.";
      }

      this.commonService.notify("Purge Audit Trail", msg, result);
    });
  }

  ImportClients()
  {
    const confirmDialogRef: MatDialogRef<ConfirmationDialog> = this.confirmDialog.open(ConfirmationDialog, { disableClose: false });
    confirmDialogRef.componentInstance.confirmMessage = `Are you sure you want to import clients?  It may take a while.`;
    confirmDialogRef.componentInstance.confirmTitle = "Import Clients?";

    //Do not need to unsubscribe as afterClose completes itself.
    confirmDialogRef.afterClosed().subscribe(result =>
    {
      if (result)
      {
        const startdate: any = this.form.get("ClientsStartDate").value;
        const interfaceImport: InterfaceImport = new InterfaceImport;
        interfaceImport.StartDate = startdate;

        this.controlPanelService.importClientsAsync(interfaceImport);
        this.commonService.SubscribeToMessages();
      }
    });
  }

  ImportRates()
  {
    const confirmDialogRef: MatDialogRef<ConfirmationDialog> = this.confirmDialog.open(ConfirmationDialog, { disableClose: false });
    confirmDialogRef.componentInstance.confirmMessage = `Are you sure you want to import rates?  It may take a while.`;
    confirmDialogRef.componentInstance.confirmTitle = "Import Rates?";

    //Do not need to unsubscribe as afterClose completes itself.
    confirmDialogRef.afterClosed().subscribe(result =>
    {
      if (result)
      {
        const interfaceImport: InterfaceImport = new InterfaceImport;
        this.controlPanelService.importRatesAsync(interfaceImport);
        this.commonService.SubscribeToMessages();
      }
    });
  }

  ImportUsers()
  {
    const confirmDialogRef: MatDialogRef<ConfirmationDialog> = this.confirmDialog.open(ConfirmationDialog, { disableClose: false });
    confirmDialogRef.componentInstance.confirmMessage = `Are you sure you want to import users?  It may take a while.`;
    confirmDialogRef.componentInstance.confirmTitle = "Import Users?";

    //Do not need to unsubscribe as afterClose completes itself.
    confirmDialogRef.afterClosed().subscribe(result =>
    {
      if (result)
      {
        const interfaceImport: InterfaceImport = new InterfaceImport;
        this.controlPanelService.importUsersAsync(interfaceImport);
        this.commonService.SubscribeToMessages();
      }
    });
  }

  ImportActivities()
  {
    const confirmDialogRef: MatDialogRef<ConfirmationDialog> = this.confirmDialog.open(ConfirmationDialog, { disableClose: false });
    confirmDialogRef.componentInstance.confirmMessage = `Are you sure you want to import activities?  It may take a while.`;
    confirmDialogRef.componentInstance.confirmTitle = "Import Activities?";

    //Do not need to unsubscribe as afterClose completes itself.
    confirmDialogRef.afterClosed().subscribe(result =>
    {
      if (result)
      {
        const startdate: any = this.form.get("ActivityStartDate").value;
        const interfaceImport: InterfaceImport = new InterfaceImport;
        interfaceImport.StartDate = startdate;

        this.controlPanelService.importActivitiesAsync(interfaceImport);
        this.commonService.SubscribeToMessages();
      }
    });
  }

  ImportProductCategories()
  {
    const confirmDialogRef: MatDialogRef<ConfirmationDialog> = this.confirmDialog.open(ConfirmationDialog, { disableClose: false });
    confirmDialogRef.componentInstance.confirmMessage = `Are you sure you want to import client categories?  It may take a while.`;
    confirmDialogRef.componentInstance.confirmTitle = "Import Client Categories?";

    //Do not need to unsubscribe as afterClose completes itself.
    confirmDialogRef.afterClosed().subscribe(result =>
    {
      if (result)
      {
        const interfaceImport: InterfaceImport = new InterfaceImport;
        this.controlPanelService.importProductCategoriesAsync(interfaceImport);
        this.commonService.SubscribeToMessages();
      }
    });
  }





  ViewLogFiles()
  {
    if (!this.dialogIsOpen)
    {
      try
      {
        this.dialogIsOpen = true;

        const dialogConfig = new MatDialogConfig();

        dialogConfig.disableClose = false;
        dialogConfig.autoFocus = true;
        dialogConfig.width = "95vw";
        dialogConfig.height = "90vh";
        dialogConfig.hasBackdrop = true;

        const dialogRef = this.dialog.open(LogFileViewerComponent, dialogConfig);

        dialogRef.afterClosed().subscribe((result: any) =>
        {
          this.dialogIsOpen = false;
        });
      }
      catch (e)
      {
        this.dialogIsOpen = false;
      }
    }

  }








}
